import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../../utils/supabase";
import { Sticker } from "../../types";
import OptimizedImage from "../OptimizedImage";

interface StickerSelectionControlsProps {
  onAddSticker: (stickerUrl: string, stickerName: string) => void;
}

const StickerSelectionControls: React.FC<StickerSelectionControlsProps> = ({
  onAddSticker,
}) => {
  const [stickers, setStickers] = useState<Sticker[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    loadStickers();
  }, []); // Empty dependency array ensures this only runs once on mount

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const loadStickers = async () => {
    // Prevent loading if already loaded
    if (hasLoadedRef.current) return;

    hasLoadedRef.current = true;
    setLoading(true);
    try {
      const { data, error } = await supabase.from("stickers").select("*");

      if (error) throw error;
      // Shuffle immediately before setting state to avoid visual flash
      const shuffledStickers = shuffleArray(data || []);
      setStickers(shuffledStickers);
    } catch (error) {
      console.error("Error loading stickers:", error);
      hasLoadedRef.current = false; // Reset on error so it can retry
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    "all",
    ...Array.from(new Set(stickers.map((s) => s.category))),
  ];
  const filteredStickers =
    selectedCategory === "all"
      ? stickers
      : stickers.filter((s) => s.category === selectedCategory);

  return (
    <div className="mb-6 p-4 bg-gray-50 rounded-lg">
      <h4 className="font-semibold text-gray-700 mb-3 text-center" dir="rtl">
        בחר סטיקר
      </h4>

      {/* Category Filter */}
      {categories.length > 1 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-1 sm:gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-2 sm:px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  selectedCategory === category
                    ? "bg-pink-500 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                dir="rtl"
              >
                {category === "all" ? "הכל" : category}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Stickers Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-500"></div>
        </div>
      ) : filteredStickers.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500 text-sm" dir="rtl">
            {selectedCategory === "all"
              ? "אין סטיקרים זמינים"
              : "אין סטיקרים בקטגוריה זו"}
          </p>
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-3 gap-2">
            {filteredStickers.map((sticker) => (
              <button
                key={sticker.id}
                onClick={() => onAddSticker(sticker.image_url, sticker.name)}
                className="w-full h-12 sm:h-14 lg:h-16 border-2 border-gray-200 rounded-lg overflow-hidden hover:border-pink-500 transition-all duration-200 transform hover:scale-105"
              >
                <OptimizedImage
                  src={sticker.image_url}
                  thumbnailSrc={sticker.thumbnail_url}
                  alt={sticker.name}
                  className="w-full h-full object-cover object-center"
                  style={{ width: "100%", height: "100%" }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "https://via.placeholder.com/100x100?text=Sticker";
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StickerSelectionControls;
